import type { AuthoredTileMapCell, AuthoredTileMapData, AuthoredTileMapLayer } from '../model/gamestate/GameState';

interface InlineRenderLayerInput {
  readonly kind: 'tile' | 'object';
  readonly name: string;
  readonly width?: number;
  readonly height?: number;
  readonly data?: readonly number[];
}

interface InlineRenderMetadata {
  readonly imagePath?: string;
  readonly tileWidth: number;
  readonly tileHeight: number;
  readonly columns?: number;
  readonly imageWidth?: number;
  readonly imageHeight?: number;
  readonly displayTileWidth?: number;
  readonly displayTileHeight?: number;
  readonly directTileImages?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly layers: readonly InlineRenderLayerInput[];
}

const TILED_FLIP_HORIZONTAL = 0x80000000;
const TILED_FLIP_VERTICAL = 0x40000000;
const TILED_FLIP_DIAGONAL = 0x20000000;
const TILED_GID_MASK = 0x1fffffff;

const renderAssetCache = new Map<string, Promise<AuthoredTileMapData>>();

export function loadInlineRenderAsset(metadata: InlineRenderMetadata): Promise<AuthoredTileMapData> {
  const cacheKey = JSON.stringify({
    imagePath: metadata.imagePath,
    tileWidth: metadata.tileWidth,
    tileHeight: metadata.tileHeight,
    columns: metadata.columns ?? null,
    imageWidth: metadata.imageWidth ?? null,
    imageHeight: metadata.imageHeight ?? null,
    displayTileWidth: metadata.displayTileWidth ?? null,
    displayTileHeight: metadata.displayTileHeight ?? null,
    directTileImages: metadata.directTileImages ?? null,
    layers: metadata.layers,
  });
  const cached = renderAssetCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const pending = loadInlineRenderAssetInternal(metadata).catch((error) => {
    renderAssetCache.delete(cacheKey);
    throw error;
  });
  renderAssetCache.set(cacheKey, pending);
  return pending;
}

async function loadInlineRenderAssetInternal(metadata: InlineRenderMetadata): Promise<AuthoredTileMapData> {
  const image = typeof metadata.imagePath === 'string'
    ? await loadImage(metadata.imagePath)
    : null;
  const directTileImages = metadata.directTileImages
    ? await loadDirectTileImages(metadata.directTileImages)
    : undefined;
  const layers = metadata.layers
    .filter((layer): layer is InlineRenderLayerInput & { kind: 'tile'; width: number; height: number; data: readonly number[] } =>
      layer.kind === 'tile'
      && typeof layer.width === 'number'
      && typeof layer.height === 'number'
      && Array.isArray(layer.data)
    )
    .map(parseLayer);

  return {
    tileWidth: metadata.tileWidth,
    tileHeight: metadata.tileHeight,
    displayTileWidth: metadata.displayTileWidth,
    displayTileHeight: metadata.displayTileHeight,
    columns: metadata.columns,
    imageWidth: metadata.imageWidth,
    imageHeight: metadata.imageHeight,
    image,
    directTileImages,
    layers,
  };
}

async function loadDirectTileImages(
  metadata: Readonly<Record<string, Readonly<Record<string, string>>>>,
): Promise<Record<string, Record<string, HTMLImageElement>>> {
  const output: Record<string, Record<string, HTMLImageElement>> = {};
  for (const [layerName, mapping] of Object.entries(metadata)) {
    output[layerName] = {};
    for (const [key, imagePath] of Object.entries(mapping)) {
      output[layerName][key] = await loadImage(imagePath);
    }
  }
  return output;
}

function parseLayer(layer: InlineRenderLayerInput & { kind: 'tile'; width: number; height: number; data: readonly number[] }): AuthoredTileMapLayer {
  const expected = layer.width * layer.height;
  if (layer.data.length !== expected) {
    throw new Error(`Render layer '${layer.name}' has ${layer.data.length} values, expected ${expected}.`);
  }
  return {
    name: layer.name,
    width: layer.width,
    height: layer.height,
    tiles: layer.data.map(parseCell),
  };
}

function parseCell(rawValue: number): AuthoredTileMapCell {
  const rawGid = Number.isFinite(rawValue) ? rawValue >>> 0 : 0;
  const gid = rawGid & TILED_GID_MASK;
  if (gid === 0) {
    return {
      gid: 0,
      flipHorizontal: false,
      flipVertical: false,
      flipDiagonal: false,
    };
  }
  return {
    gid,
    flipHorizontal: (rawGid & TILED_FLIP_HORIZONTAL) !== 0,
    flipVertical: (rawGid & TILED_FLIP_VERTICAL) !== 0,
    flipDiagonal: (rawGid & TILED_FLIP_DIAGONAL) !== 0,
  };
}

function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load image '${path}'.`));
    image.src = path;
  });
}
